/**
 * Returns true on iOS devices (iPhone, iPad, iPod) and iPadOS running as desktop.
 * Needed because iOS Safari blocks programmatic anchor.click() and blob-URL downloads.
 */
export const isIOS = () =>
  /iPhone|iPad|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

/**
 * Download a blob URL on any platform.
 *
 * - iOS: programmatic anchor.click() is silently blocked. Instead, open the
 *   blob URL in a new tab so iOS renders the PDF and the user can save it via
 *   the native share sheet.
 * - Android / Desktop: use the standard a[download] trick.
 */
export const downloadBlobUrl = (url, filename) => {
  if (isIOS()) {
    window.open(url, '_blank', 'noopener');
  } else {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
};

/**
 * Download a file that requires an async fetch (e.g. authenticated API call).
 *
 * iOS breaks the download if we await before triggering it, because the async
 * gap loses the user-gesture context. The workaround:
 *   1. Open a blank window SYNCHRONOUSLY inside the click handler (still in gesture).
 *   2. Await the fetch.
 *   3. Navigate that pre-opened window to the blob URL.
 *
 * @param {() => Promise<Blob|null>} fetchFn  - async function that returns the Blob
 * @param {string}                   filename  - desired filename (used on non-iOS)
 */
export const downloadFromFetch = async (fetchFn, filename) => {
  // Open the window synchronously — must happen before any await on iOS
  const preOpenedWindow = isIOS() ? window.open('', '_blank') : null;

  try {
    const blob = await fetchFn();
    if (!blob) { preOpenedWindow?.close(); return; }

    const url = URL.createObjectURL(blob);

    if (preOpenedWindow) {
      // Navigate the pre-opened Safari tab to the PDF
      preOpenedWindow.location.href = url;
      // Give iOS time to load before revoking
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } else {
      // Android / Desktop: standard a[download] approach
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 200);
    }
  } catch (err) {
    console.error('downloadFromFetch error:', err);
    preOpenedWindow?.close();
  }
};
